/**
 * GorillaProductOptions public interface
 * ---------------------------------------
 * init(root): idempotently enhance root (or every [data-gpo-root] below it).
 * matchSelection(products, { size, scent }): return the one exact
 *   { product, variant } match, otherwise null (missing and ambiguous fail closed).
 * getImageForSelection({ size, scent }): return { front, back } asset URLs.
 * getImageForVariant(id): return the front asset URL registered by init, or null.
 * registerCatalog(products, imageMap): register a serialized catalog without UI.
 * resolveVariant is an alias of matchSelection for product-page/cart consumers.
 *
 * Catalog input uses product.options plus variant.options in Shopify option order.
 * No variant ID, price, availability, or scent is invented by this module.
 */
(function (global) {
  'use strict';

  // The showcase may include this asset while a theme layout also loads it.
  // Preserve the first registry so a duplicate script never erases cart images.
  if (global.GorillaProductOptions) {
    if (typeof document !== 'undefined') global.GorillaProductOptions.init(document);
    if (typeof module !== 'undefined' && module.exports) module.exports = global.GorillaProductOptions;
    return;
  }

  var SIZES = ['50ml', '200ml'];
  var SCENTS = ['lavender', 'lemon', 'unscented', 'black-ice'];
  var variantImages = new Map();
  var imageMaps = [];
  var defaultImages = {};
  SIZES.forEach(function (size) {
    SCENTS.forEach(function (scent) {
      var base = 'gorilla-chalk-' + size + '-' + scent;
      defaultImages[size + '-' + scent] = {
        front: base + '-front-normalized.png',
        back: base + '-back-normalized.png'
      };
    });
  });

  function text(value) {
    return typeof value === 'string' ? value : '';
  }

  function findUnique(value, matchers) {
    var matches = matchers.filter(function (entry) {
      return entry[1].test(value);
    }).map(function (entry) {
      return entry[0];
    });
    return Array.from(new Set(matches)).length === 1 ? matches[0] : null;
  }

  function findSize(value) {
    return findUnique(text(value), [
      ['200ml', /\b200\s*(?:ml|millilit(?:er|re)s?)\b/i],
      ['50ml', /\b50\s*(?:ml|millilit(?:er|re)s?)\b/i]
    ]);
  }

  function findScent(value) {
    return findUnique(text(value), [
      ['black-ice', /\b(?:black[\s-]*ice|red)\b/i],
      ['lavender', /\b(?:lavender|purple)\b/i],
      ['lemon', /\b(?:lemon|yellow)\b/i],
      ['unscented', /\b(?:unscented|regular|no[\s-]*scent|fragrance[\s-]*free|white)\b/i]
    ]);
  }

  function productSize(product) {
    return findSize(text(product.title) + ' ' + text(product.handle)) ||
      (product.handle === 'liquid-chalk-1' ? '200ml' :
        product.handle === 'liquid-chalk' ? '50ml' : null);
  }

  function variantSelection(product, variant) {
    var names = Array.isArray(product.options) ? product.options : [];
    var values = Array.isArray(variant.options) ? variant.options : [];
    var sizeValue = null;
    var scentValue = null;
    names.forEach(function (name, index) {
      if (/\bsize\b/i.test(text(name))) sizeValue = text(values[index]);
      if (/\b(?:scent|fragrance|aroma|colour|color)\b/i.test(text(name))) {
        scentValue = text(values[index]);
      }
    });
    var variantText = text(variant.title) + ' ' + values.join(' ');
    var size = findSize(sizeValue || variantText) || productSize(product);
    var scent = findScent(scentValue || variantText);
    // A product title may explicitly identify a one-variant scent product.
    // "Default Title" itself never implies a scent.
    if (!scent && !scentValue) scent = findScent(text(product.title) + ' ' + text(product.handle));
    return size && scent ? { size: size, scent: scent } : null;
  }

  function uniqueProducts(products) {
    var seen = new Set();
    return (Array.isArray(products) ? products : []).filter(function (product) {
      if (!product || !product.id || seen.has(String(product.id))) return false;
      seen.add(String(product.id));
      return true;
    });
  }

  function matchSelection(products, selection) {
    // Explicit Theme Editor assignments take precedence over name inference.
    // Empty slots remain in the catalog, so a missing assignment never falls
    // through to a similarly named but unintended product.
    if (Array.isArray(products) && products.some(function (product) { return product && product.selection; })) {
      var assigned = products.filter(function (product) {
        return product && product.selection && product.selection.size === selection.size &&
          product.selection.scent === selection.scent;
      });
      if (assigned.length !== 1 || !assigned[0].id) return null;
      var product = assigned[0];
      if (products.some(function (other) {
        return other && other !== product && String(other.id) === String(product.id);
      })) return null;
      var variant = (product.variants || []).find(function (candidate) {
        return candidate.id && String(candidate.id) === String(product.selected_variant_id);
      });
      return variant ? { product: product, variant: variant } : null;
    }
    var matches = [];
    uniqueProducts(products).forEach(function (product) {
      (Array.isArray(product.variants) ? product.variants : []).forEach(function (variant) {
        var resolved = variantSelection(product, variant);
        if (resolved && resolved.size === selection.size && resolved.scent === selection.scent) {
          matches.push({ product: product, variant: variant });
        }
      });
    });
    return matches.length === 1 ? matches[0] : null;
  }

  function readJSON(root, selector) {
    var node = root.querySelector(selector);
    if (!node) return null;
    try {
      return JSON.parse(node.textContent || '');
    } catch (error) {
      return null;
    }
  }

  function imageForSelection(selection, imageMap) {
    var map = imageMap || imageMaps[imageMaps.length - 1] || defaultImages;
    return map && map[selection.size + '-' + selection.scent] || null;
  }

  function setBottle(card, selection, imageMap) {
    var pair = imageForSelection(selection, imageMap);
    if (!pair) return;
    var label = selection.size + ' ' + scentLabel(selection.scent);
    card.querySelectorAll('[data-gpo-front]').forEach(function (image) {
      image.src = pair.front;
      image.alt = label + ', front view';
    });
    card.querySelectorAll('[data-gpo-back]').forEach(function (image) {
      image.src = pair.back;
      image.alt = label + ', back view';
    });
    var bottle = card.querySelector('[data-bottle]');
    if (bottle) {
      bottle.setAttribute('aria-label', label + ' product images');
    }
    showBottleView(card, 'front', true);
  }

  function previewBottleView(card, view) {
    var bottle = card.querySelector('[data-bottle]');
    var back = view === 'back';
    if (bottle) bottle.classList.toggle('is-flipped', back);
    var stage = card.querySelector('[data-gpo-stage]');
    if (stage) stage.setAttribute('aria-label', back ? 'Flip to front view' : 'Flip to back view');
  }

  function showBottleView(card, view, persist) {
    previewBottleView(card, view);
    var bottle = card.querySelector('[data-bottle]');
    if (bottle && persist) bottle.setAttribute('data-gpo-selected-view', view);
    card.querySelectorAll('[data-gpo-view]').forEach(function (button) {
      button.setAttribute('aria-pressed', String(button.getAttribute('data-gpo-view') === view));
    });
  }

  function scentLabel(scent) {
    return {
      lavender: 'Lavender',
      lemon: 'Lemon',
      unscented: 'Regular / Unscented',
      'black-ice': 'Black Ice'
    }[scent] || scent;
  }

  function money(cents, currency) {
    var amount = Number(cents) / 100;
    if (!Number.isFinite(amount)) return '';
    try {
      return new Intl.NumberFormat(undefined, {
        style: 'currency',
        currency: currency || 'USD'
      }).format(amount);
    } catch (error) {
      return '$' + amount.toFixed(2);
    }
  }

  function setText(card, selector, value) {
    var node = card.querySelector(selector);
    if (node) node.textContent = value;
  }

  function updateCard(card, products, imageMap) {
    var selection = {
      size: card.getAttribute('data-size'),
      scent: card.getAttribute('data-scent')
    };
    var match = matchSelection(products, selection);
    var available = Boolean(match && match.product.available && match.variant.available);
    setBottle(card, selection, imageMap);
    setText(card, '[data-gpo-title]', match ? text(match.product.title) :
      (selection.size === '200ml' ? '200ML BEAST' : '50ML CARABINER'));
    setText(card, '[data-gpo-subtitle]', selection.size === '200ml' ? 'LIQUID CHALK BOTTLE' : 'TRAVEL SIZE');
    setText(card, '[data-gpo-description]', selection.size === '200ml'
      ? 'The anchor of your gym bag. Engineered for dumbbells, kettlebells, rings, and heavy grip work. One application lasts through your heaviest sets.'
      : 'Clip it to your bag, your harness, or your belt. The 50ml goes wherever you train. Outdoor calisthenics, rock climbing, or the local commercial gym.');
    if (match) {
      card.setAttribute('data-product-id', match.product.id);
      card.setAttribute('data-variant-id', match.variant.id);
    } else {
      card.removeAttribute('data-product-id');
      card.removeAttribute('data-variant-id');
    }
    setText(card, '[data-gpo-scent-feature]', scentLabel(selection.scent) + '.');
    var productLink = card.querySelector('[data-gpo-product-link]');
    if (productLink) {
      var productUrl = match && text(match.product.url);
      if (productUrl && productUrl.charAt(0) === '/') {
        productLink.href = productUrl;
        productLink.textContent = 'View ' + text(match.product.title);
        productLink.hidden = false;
      } else {
        productLink.hidden = true;
        productLink.removeAttribute('href');
        productLink.textContent = '';
      }
    }

    card.querySelectorAll('[data-gpo-choice]').forEach(function (button) {
      var selected = button.getAttribute('data-gpo-value') === selection[button.getAttribute('data-gpo-choice')];
      button.setAttribute('aria-pressed', String(selected));
    });

    var status = card.querySelector('[data-gpo-stock]');
    if (status) {
      status.setAttribute('data-status', !match ? 'unconfigured' : available ? 'available' : 'soldout');
      setText(status, '[data-gpo-stock-label]', !match ? 'Not configured in Shopify' : available ? 'Available now' : 'Sold out');
    }
    var button = card.querySelector('[data-gpo-add]');
    if (button) {
      button.disabled = !available;
      if (!match) {
        button.textContent = 'NOT CONFIGURED';
        button.removeAttribute('data-add-to-cart');
      } else if (!available) {
        button.textContent = 'SOLD OUT';
        button.removeAttribute('data-add-to-cart');
      } else {
        button.textContent = card.getAttribute('data-add-label') + ' — ' + money(match.variant.price, match.product.currency);
        button.setAttribute('data-add-to-cart', JSON.stringify([{ id: match.variant.id, quantity: 1 }]));
      }
    }
    card._gpoMatch = match;
    card.dispatchEvent(new CustomEvent('gorilla:selection-change', {
      bubbles: true,
      detail: { selection: selection, match: match }
    }));
  }

  function updateBundle(root) {
    var cards = Array.from(root.querySelectorAll('[data-gpo-card]'));
    var bundle = root.querySelector('[data-gpo-bundle]');
    if (!bundle || cards.length < 2) return;
    var matches = cards.slice(0, 2).map(function (card) { return card._gpoMatch; });
    var configured = matches.every(Boolean);
    var available = configured && matches.every(function (match) {
      return match.product.available && match.variant.available;
    });
    var button = bundle.querySelector('[data-gpo-bundle-add]');
    var price = bundle.querySelector('[data-gpo-bundle-price]');
    if (price) {
      price.textContent = configured
        ? money(Number(matches[0].variant.price) + Number(matches[1].variant.price), matches[0].product.currency)
        : '—';
    }
    if (!button) return;
    button.disabled = !available;
    if (!configured) {
      button.textContent = 'NOT CONFIGURED';
      button.removeAttribute('data-add-to-cart');
    } else if (!available) {
      button.textContent = 'SOLD OUT';
      button.removeAttribute('data-add-to-cart');
    } else {
      button.textContent = bundle.getAttribute('data-bundle-label');
      button.setAttribute('data-add-to-cart', JSON.stringify(matches.map(function (match) {
        return { id: match.variant.id, quantity: 1 };
      })));
    }
  }

  function enhanceRoot(root) {
    if (!root || root.getAttribute('data-gpo-initialized') === 'true') return root;
    var products = readJSON(root, '[data-gpo-catalog]');
    var imageMap = readJSON(root, '[data-gpo-images]');
    if (!Array.isArray(products) || !imageMap) return root;
    // Decode all fixed artwork up front so switching does not wait for the
    // previously unseen scent/back image to download.
    Object.keys(imageMap).forEach(function (key) {
      ['front', 'back'].forEach(function (view) {
        var image = new Image();
        image.src = imageMap[key][view];
      });
    });
    registerCatalog(products, imageMap);
    root.querySelectorAll('[data-gpo-card]').forEach(function (card) {
      card.querySelectorAll('[data-gpo-choice]').forEach(function (button) {
        button.disabled = false;
        button.addEventListener('click', function () {
          card.setAttribute('data-' + button.getAttribute('data-gpo-choice'), button.getAttribute('data-gpo-value'));
          updateCard(card, products, imageMap);
          updateBundle(root);
        });
      });
      card.querySelectorAll('[data-gpo-view]').forEach(function (button) {
        button.addEventListener('click', function (event) {
          event.stopPropagation();
          showBottleView(card, button.getAttribute('data-gpo-view'), true);
        });
      });
      var bottle = card.querySelector('[data-bottle]');
      var stage = card.querySelector('[data-gpo-stage]');
      if (bottle) {
        bottle.addEventListener('mouseenter', function () {
          previewBottleView(card, 'back');
        });
        bottle.addEventListener('mouseleave', function () {
          previewBottleView(card, bottle.getAttribute('data-gpo-selected-view') || 'front');
        });
      }
      if (stage) {
        var toggleStage = function () {
          var selected = bottle && bottle.getAttribute('data-gpo-selected-view') || 'front';
          showBottleView(card, selected === 'back' ? 'front' : 'back', true);
        };
        stage.addEventListener('click', toggleStage);
        stage.addEventListener('keydown', function (event) {
          if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            toggleStage();
          }
        });
      }
      updateCard(card, products, imageMap);
    });
    updateBundle(root);
    root.setAttribute('data-gpo-initialized', 'true');
    return root;
  }

  function registerDataScripts(scope) {
    if (!scope || !scope.querySelectorAll) return;
    scope.querySelectorAll('[data-gpo-global-catalog]:not([data-gpo-registered])').forEach(function (node) {
      var container = node.parentElement || scope;
      var images = readJSON(container, '[data-gpo-global-images]');
      try {
        var products = JSON.parse(node.textContent || '');
        if (Array.isArray(products) && images) {
          registerCatalog(products, images);
          node.setAttribute('data-gpo-registered', 'true');
        }
      } catch (error) {
        // Invalid or incomplete Liquid JSON deliberately registers nothing.
      }
    });
  }

  function registerCatalog(products, imageMap) {
    if (imageMap) imageMaps.push(imageMap);
    SIZES.forEach(function (size) {
      SCENTS.forEach(function (scent) {
        var match = matchSelection(products, { size: size, scent: scent });
        var pair = imageForSelection({ size: size, scent: scent }, imageMap);
        if (match && pair) variantImages.set(String(match.variant.id), pair.front);
      });
    });
    return variantImages;
  }

  function init(root) {
    var scope = root || document;
    registerDataScripts(scope);
    if (scope.matches && scope.matches('[data-gpo-root]')) enhanceRoot(scope);
    if (scope.querySelectorAll) scope.querySelectorAll('[data-gpo-root]').forEach(enhanceRoot);
    return scope;
  }

  var api = {
    init: init,
    matchSelection: matchSelection,
    resolveVariant: matchSelection,
    registerCatalog: registerCatalog,
    getImageForSelection: function (selection) { return imageForSelection(selection); },
    getImageForVariant: function (id) { return variantImages.get(String(id)) || null; },
    _variantSelection: variantSelection
  };
  global.GorillaProductOptions = api;
  if (typeof module !== 'undefined' && module.exports) module.exports = api;
  if (typeof document !== 'undefined') {
    if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', function () { init(document); });
    else init(document);
    document.addEventListener('shopify:section:load', function (event) { init(event.target); });
  }
})(typeof window !== 'undefined' ? window : globalThis);